import { addEventToDatalayer, processStoredPiwikdimensions, createHash, addDimensionToDatalayer, putDimensionInDatalayer, putCusDimInDatalayer } from '../../@helpers/js/datalayer.js';
import { isUrlExternal } from '../../@helpers/js/helpers.js';
import { getStorage } from '../../@helpers/js/keyvaluestorage.js';
import { processEvent } from '../../@helpers/js/queue-processing.js';
import { triggerNamedNotify } from '../../@helpers/js/observer.js';
import { wait } from '../../@helpers/js/wait.js';

function Datalayer(element) {
	const _element = element || document;

	const buttonCategory = 'button click';
	const internalLinkCategory = 'internal link click';
	const externalLinkCategory = 'external link click';
	const skipLinkCategory = 'skiplink click';
	const revealerCategory = 'revealer click';
	const headerLinkCategory = 'header link click';
	const menuLinkCategory = 'menu link click';
	const winkelmandjeCategory = 'winkelmandje click';
	const footerLinkCategory = 'footer link click';

	const anchorSelector = ':is(#content, .mfa-app) :is(.c-link, a)';
	const ctaStoreButtonSelector = '[class*=__store-buttons] a';
	const mfaButtonSelector = '.mfa-app [id^="kaart-"] button';
	const buttonSelector = ':is(#content, .mfa-app) :is(button, .c-button, input[type=button], input[type=submit], input[type=reset])';
	const headerLinkSelector = '.shared-header__nav-domain a';
	const menuLinkSelector = ':is(.shared-header__nav, .just-navigation__menu) a[href]';
	const menuButtonSelector = ':is(.shared-header__nav, .just-navigation__menu) :is(button, a:has(+ .just-navigation__submenu))';
	const winkelmandjeSelector = 'a.shared-header__btn--shopping-cart';
	const mijnLinkSelector = '.just-navigation__mijn-just a[href], a:is(.shared-header__btn-mobile--account, .shared-header__btn-desktop-account)';
	const revealerSelector = '.c-revealer button.c-revealer__title';
	const footerLinkSelector = '#footer a';
	const skipLinkSelector = '.skiplinks a';
	const mfaAppSelector = '.mfa-app';

	function clickHandler(el) {
		if (!el) return;

		const event = {
			event: 'event',
		}

		const skiplink = el.closest(skipLinkSelector);
		if (skiplink) {
			event.event_category = skipLinkCategory;
			event.event_action = skiplink.textContent?.trim?.() || 'skiplink zonder tekst';
			event.event_name = skiplink.href?.trim?.() || 'skiplink zonder href';
			addEventToDatalayer(event);
			return;
		}

		const ctaStoreButton = el.closest(ctaStoreButtonSelector);
		if (ctaStoreButton) {
			event.event_category = buttonCategory;
			event.event_action = ctaStoreButton.querySelector('img')?.getAttribute('alt') || 'CTA AppStore button';
			event.event_name = ctaStoreButton.href?.trim?.() || '';
			addEventToDatalayer(event);
			return;
		}

		const revealer = el.closest(revealerSelector);
		if (revealer) {
			event.event_category = revealerCategory;
			event.event_action = `${revealer.ariaExpanded === "true" ? 'Open' : 'Close'} ${revealer.textContent?.trim?.() || 'revealer zonder tekst'}`;
			event.event_name = '';
			addEventToDatalayer(event);
			return;
		}

		const button = el.closest(buttonSelector);
		if (button) {
			event.event_category = buttonCategory;
			event.event_action = (button.textContent || button.value)?.trim?.() || 'button zonder tekst';
			event.event_name = (button.href || button.dataset?.eventName)?.trim?.() || '';
			addEventToDatalayer(event);
			return;
		}

		const anchor = el.closest(anchorSelector);
		if (anchor) {
			event.event_category = isUrlExternal(anchor.getAttribute('href')) ? externalLinkCategory : internalLinkCategory;
			event.event_action = anchor.textContent?.trim?.() || 'link zonder tekst';
			event.event_name = anchor.href?.trim?.() || '';
			addEventToDatalayer(event);
			return;
		}

		const headerLink = el.closest(headerLinkSelector);
		if (headerLink?.href) {
			event.event_category = headerLinkCategory;
			event.event_action = headerLink.textContent?.trim?.() || 'headerlink zonder tekst';
			event.event_name = headerLink.href?.trim?.() || 'headerlink zonder href';
			addEventToDatalayer(event);
			return;
		}

		const footerLink = el.closest(footerLinkSelector);
		if (footerLink?.href) {
			event.event_category = footerLinkCategory;
			event.event_action = footerLink.textContent?.trim?.() || 'footerlink zonder tekst';
			event.event_name = footerLink.href?.trim?.() || 'footerlink zonder href';
			addEventToDatalayer(event);
			return;
		}
		
		const menuLink = el.closest(menuLinkSelector);
		if (menuLink) {
			event.event_category = menuLinkCategory;
			event.event_action = menuLink.textContent?.trim?.() || 'menulink zonder tekst';
			event.event_name = menuLink.href?.trim?.() || '';
			addEventToDatalayer(event);
			return;
		}

		const winkelMandjeLink = el.closest(winkelmandjeSelector);
		if (winkelMandjeLink) {
			event.event_category = winkelmandjeCategory;
			event.event_action = winkelMandjeLink.textContent?.trim?.() || 'winkelmandje zonder tekst';
			event.event_name = winkelMandjeLink.href?.trim?.() || '';
			addEventToDatalayer(event);
			return;
		}
		
		const menuButton = el.closest(menuButtonSelector);
		if (menuButton) {
			event.event_category = internalLinkCategory;
			event.event_action = menuButton.textContent?.trim?.() || 'menubutton zonder tekst';
			event.event_name = menuButton.href?.trim?.() || '';
			addEventToDatalayer(event);
			return;
		}
		
		const mijnLink = el.closest(mijnLinkSelector);
		if (mijnLink) {
			event.event_category = externalLinkCategory;
			event.event_action = mijnLink.textContent?.trim?.() || 'externallink zonder tekst';
			event.event_name = mijnLink.href?.trim?.() || '';
			addEventToDatalayer(event);
			return;
		}

		return undefined;
	}
	const clickProcessing = processEvent(async (elmnt) => clickHandler(elmnt));

	_element.addEventListener('click', (clickEvent) => {
		if (clickEvent?.target) {
			const hrefTarget = clickEvent.target?.closest('a[href]:not([download], [href^="mailto:"], [href^="tel:"], [href^="sms:"]), area[href]');
			if (clickEvent?.defaultPrevented === false && hrefTarget?.hasAttribute('href') && !hrefTarget.getAttribute('href').startsWith?.('#') && clickEvent.target.href !== location.href) {
				clickHandler(hrefTarget);
			} else {
				const activeMfa = clickEvent.target?.closest(mfaAppSelector);
				if (activeMfa) {
					let sidepanelMfaClick = clickEvent.target?.closest('cz-mfaloader, [role="dialog"]');
					let choice = clickEvent.target?.closest('input[type=radio]');
					let actionButton = clickEvent.target?.closest('button');
					let choiceLabel = clickEvent.target?.closest('label[for]');
					if (choice) {
						choiceLabel = choice.parentElement?.querySelector(`label[for="${choice.getAttribute('id')}"]`);
					} else if (choiceLabel) {
						choice = choiceLabel.parentElement?.querySelector(`input[type=radio][id="${choiceLabel.getAttribute('for')}"]`);
					}
					if (choice && choiceLabel) {
						const productType = choice.name?.includes('additional') ? 'AV' : choice.name?.includes('dental') ? 'TV' : choice.name?.includes('basic') ? 'BV' : '';
						const insuranceProduct = choice?.closest('div[class^=InsuranceProductstyled__InsuranceProduct]');
						if (insuranceProduct) {
							const event_category = `${actionButton ? buttonCategory : 'click'}`;
							const event_action = `${actionButton ? (actionButton.textContent || actionButton.value)?.trim?.() || 'button zonder tekst' : insuranceProduct.closest('#kaart-1') ? 'stap 1 BV' : insuranceProduct.closest('#kaart-2') ? 'stap 2' : insuranceProduct.closest('#kaart-3') ? 'stap 3' : `meeverzekerde${productType ? ' ' + productType : ''}`}`;
							const event_name = (insuranceProduct.closest('[class*="NoProduct"]') ? choiceLabel.querySelector('[class*="MainContainer"]')?.textContent?.trim?.() : choiceLabel.querySelector('h3, h4')?.textContent?.trim?.()) || event_action;
							const event = {
								event: 'event',
								event_category,
								event_action,
								event_name
							}
							const verzekerdeType = `${actionButton ? '' : activeMfa.hasAttribute('id') ? 'HV' : 'MV'}`;
							if (verzekerdeType) {
								putCusDimInDatalayer(15, verzekerdeType);
								event.customDimensions = {
									'dimension15': verzekerdeType,
								};
							}
							addEventToDatalayer(event);
						} else if (choice.className?.startsWith('Deduct')) {
							const event_category = 'click'
							const event_action = activeMfa.hasAttribute('id') ? 'stap 1 ER' : 'meeverzekerde ER';
							const event_name = choiceLabel.textContent?.trim?.() || event_action;
							const event = {
								event: 'event',
								event_category,
								event_action,
								event_name
							}
							event.customDimensions = {
								'dimension15': `${activeMfa.hasAttribute('id') ? 'HV' : 'MV'}`,
							};
							putCusDimInDatalayer(15, `${activeMfa.hasAttribute('id') ? 'HV' : 'MV'}`);
							addEventToDatalayer(event);
						}
					} else if (actionButton) {
						if (!sidepanelMfaClick) {
							const mfaButton = actionButton.closest(mfaButtonSelector);
							const label = mfaButton?.closest('label');
							const card = mfaButton?.closest('[id^="kaart-"]')
							const eventName = card?.querySelector('h2')?.textContent?.trim?.() || card?.id?.replace(/kaart-/i, 'stap ') || '';
							if (mfaButton && card && !label) {
								const event_category = buttonCategory;
								const event_action = (mfaButton.textContent || mfaButton.value)?.trim?.() || 'button zonder tekst';
								const event_name = eventName;
								const event = {
									event: 'event',
									event_category,
									event_action,
									event_name
								}
								addEventToDatalayer(event);
								document.querySelector('button[class*=Drawerstyled__CloseBtn]')?.setAttribute('data-event-name', eventName);
								activeMfa?.setAttribute('data-event-name', eventName);
								return;
							}
						} else {
							let removeButton = actionButton.closest('[class*=RemoveButton]');
							if (removeButton) {
								let eventName = removeButton.parentElement?.querySelector('span[class*=CategoryTitle]')?.textContent?.trim?.() || removeButton.parentElement?.querySelector('button[class*=Summary]')?.textContent?.trim?.() || '';
								const event_category = buttonCategory;
								const event_action = (removeButton.textContent || removeButton.value)?.trim?.() || 'button zonder tekst';
								const event_name = eventName;
								const event = {
									event: 'event',
									event_category,
									event_action,
									event_name
								}
								addEventToDatalayer(event);
								return;
							}
							let showDetailsButtonInTable = actionButton.closest('[class*=ShowDetailsButton]');
							if (showDetailsButtonInTable) {
								let eventName = showDetailsButtonInTable.parentElement?.querySelector('span[class*=CategoryTitle]')?.textContent?.trim?.() || '';
								let tableRow = showDetailsButtonInTable.closest('tr[class*=TableRow]');
								let hiddenDetails = tableRow?.querySelector('[style*="visibility:"]')?.style?.visibility === 'hidden';
								const event_category = buttonCategory;
								const event_action = `details ${hiddenDetails ? 'close' : 'open'}`;
								const event_name = eventName;
								const event = {
									event: 'event',
									event_category,
									event_action,
									event_name
								}
								addEventToDatalayer(event);
								return;
							}
							let addVergoedingeButton = actionButton.closest('[class*=SearchDropdownstyled__ButtonSubmit]');
							if (addVergoedingeButton) {
								let lastTableRow = activeMfa.querySelector('[class*=CompareReimbursementTablestyled__Table] > tbody')?.lastElementChild;
								let eventName = lastTableRow?.querySelector('span[class*=CategoryTitle]')?.textContent?.trim?.() || '';
								const event_category = buttonCategory;
								const event_action = (addVergoedingeButton.textContent || addVergoedingeButton.value)?.trim?.() || 'button zonder tekst';
								const event_name = eventName;
								const event = {
									event: 'event',
									event_category,
									event_action,
									event_name
								}
								addEventToDatalayer(event);
								return;
							}
							clickHandler(actionButton);
						}
					} else {
						let showDetailsCheckboxAccordion = clickEvent.target.closest('input[class*=Switchstyled__Input]');
						if (showDetailsCheckboxAccordion) {
							let accordion = showDetailsCheckboxAccordion.closest('[class*=Accordionstyled__Wrapper]');
							let eventName = accordion?.querySelector('button[class*=Summary]')?.textContent?.trim?.() || '';
							let showingDetails = showDetailsCheckboxAccordion.checked;
							const event_category = buttonCategory;
							const event_action = `details ${showingDetails ? 'open' : 'close'}`;
							const event_name = eventName;
							const event = {
								event: 'event',
								event_category,
								event_action,
								event_name
							}
							addEventToDatalayer(event);
							return;
						}
						let filterListItem = clickEvent.target.closest('div[class*=ListFilterstyled__FilterList] input[type=checkbox]');
						if (filterListItem) {
							let eventName = filterListItem?.labels?.[0]?.textContent?.trim?.() || '';
							let showAV = filterListItem.checked;
							const event_category = buttonCategory;
							const event_action = `filter ${showAV ? 'on' : 'off'}`;
							const event_name = eventName;
							const event = {
								event: 'event',
								event_category,
								event_action,
								event_name
							}
							addEventToDatalayer(event);
							return;
						}
					}
				} else {
					let sidePanelButton = clickEvent.target.closest('cz-sidepanel button:where([slot=trigger], [slot=close])');
					let eventName = location.href;
					if (sidePanelButton) {
						const event_category = buttonCategory;
						const event_action = (sidePanelButton.textContent || sidePanelButton.value)?.trim?.() || 'button zonder tekst';
						const event_name = eventName;
						const event = {
							event: 'event',
							event_category,
							event_action,
							event_name
						}
						addEventToDatalayer(event);
						return;
					}
					clickProcessing.push(clickEvent.target);
				}
			}
		}
	});

	const store = getStorage('session');
	const mainMfa = _element.querySelector('.mfa-app[id]');

	if (mainMfa) {
		const brand = mainMfa.getAttribute('brand');

		const processAnalysis = async (result) => {
			if (result.ok) {
				try {					
					const body = await result.json();

					if (body?.Overeenkomst) {
						const {TotaalPerMaand, InclusiefCollectiviteitsKorting, BasisVerzekering : basis, Collectiviteit : coll, Verzekerden} = body.Overeenkomst;
						let basic = {
							sku: basis?.Code || 'BV',
							name: basis?.Name,
							category: "Basisverzekering",
							price: basis?.Premie?.replace(',','.'),
							brand: brand?.toUpperCase(),
							variant: basis?.Type,
						}
						const collectivity = {
							number: coll?.CollectiviteitsNummer?.Value,
							name: coll?.CollectiviteitsNaam,
							isDefault: coll?.IsDefaultCollectivity,
						};

						if (!collectivity.isDefault && collectivity.number) {
							putCusDimInDatalayer(26, collectivity.number);
							addDimensionToDatalayer('collectiviteit', collectivity.number);
						}
						const intermediary = {
							number: coll?.IntermediairNummer,
							name: coll?.IntermediairNaam,
						};
						putCusDimInDatalayer(14, `${(!collectivity.isDefault && collectivity.number !== '000000019') ? collectivity.number === '004832558' ? 'ZZP' : intermediary.name ? 'collectief via intermediair' : 'collectief' : 'individueel'}`);
						addDimensionToDatalayer('kanaal', `${(!collectivity.isDefault && collectivity.number !== '000000019') ? collectivity.number === '004832558' ? 'ZZP' : intermediary.name ? 'collectief via intermediair' : 'collectief' : 'individueel'}`);

						const verzekerden = Verzekerden?.Verzekeringsnemer && Verzekerden?.Medeverzekerden ? [Verzekerden.Verzekeringsnemer, ...Verzekerden.Medeverzekerden] : [];
						const products = verzekerden?.reduce((sum, verzekerde, i) => {
							const {AanvullendeVerzekering:av, TandartsVerzekering:tv, EigenRisico:er} = verzekerde;
							const verzekerdeType = verzekerde?.Type === 'Hoofdverzekerde' ? 'HV' : `MV${i}`;
							putDimensionInDatalayer('type_verzekerde', verzekerdeType);
							basic.variant = `ER ${er?.Bedrag}`;
							basic.customDimensions = {
								'14': `${(!collectivity.isDefault && collectivity.number !== '000000019') ? collectivity.number === '004832558' ? 'ZZP' : intermediary.name ? 'collectief via intermediair' : 'collectief' : 'individueel'}`,
								'15': verzekerdeType,
								'3': collectivity.number,
							}
							sum.push(basic);
							if (av?.Code) {
								const aanv = {
									sku: av?.Code || 'AV',
									name: av?.Name,
									category: "Aanvullendeverzekering",
									price: av?.Premie?.replace(',','.'),
									brand: brand?.toUpperCase(),
									variant: `${av?.HasDentalInsurance ? 'Inclusief TV' : 'AV'}`,
									customDimensions: {
										'15': verzekerdeType,
									}
								}
								sum.push(aanv);
							}
							if (tv?.Code) {
								const tand = {
									sku: tv?.Code || 'TV',
									name: tv?.Name,
									category: "Tandartsverzekering",
									price: tv?.Premie?.replace(',','.'),
									brand: brand?.toUpperCase(),
									variant: 'TV',
									customDimensions: {
										'15': verzekerdeType,
									}
								}
								sum.push(tand);
							}
							return sum;
						}, []);

						if (basic.name) {
							const basket = {
								products,
								total: TotaalPerMaand?.replace(',','.'),
								discount: InclusiefCollectiviteitsKorting?.replace(',','.'),
							}
							store.storeKeyValue(`shoppingCart`, basket);
							window._paq?.push(['ecommerceCartUpdate', basket.products, basket.total]);
							triggerNamedNotify('shoppingcart-update', {
								name: 'shoppingcart-update',
								args: {
									premie: TotaalPerMaand || '',
									discount: !!(body?.Betalingsgegevens?.IBAN && body?.Betalingsgegevens?.Betaaltermijn?.toLowerCase() !== 'maand'),
								},
							});
						}
					}
					if (body?.Contactgegevens?.Email) {
						const emailHash = await createHash(body.Contactgegevens.Email);
						putCusDimInDatalayer(2, emailHash);
						addDimensionToDatalayer('user_id', emailHash);
					}
				} catch (error) {
					console.warn(error);
				}
			}
		}

		let fetchHandler = {
			apply: async function(target, thisArg, argumentsList) {
				const url = argumentsList?.[0];
				const { method } = argumentsList?.length > 1 ? argumentsList[1] : {};
				if (url?.toLowerCase()?.endsWith('getuserselections')) {
					let prom = target.apply(thisArg, argumentsList);

					try {
						let resp = await prom;
						let result = resp.clone();
						setTimeout(() => processAnalysis(result), 10);
					} catch (error) {
						console.warn(error);
					}
					return prom;
				} else if (url?.toLowerCase()?.endsWith('aanmelden') && method?.toLowerCase() === 'post') {
					store.storeKeyValue('confirmation', 'aanmelden');
					let prom = target.apply(thisArg, argumentsList);

					try {
						let resp = await prom;
						let result = resp.clone();
						if (result.ok) {
							let body = await result.json();
							store.storeKeyValue('aanmeldId', body?.Model?.AanmeldId);
						}
					}
					catch(error) {
						console.warn(error);
					}
					return prom;
				} else if (url?.toLowerCase()?.endsWith('aanmelden/premies') && method?.toLowerCase() === 'post') {
					let prom = target.apply(thisArg, argumentsList);

					try {
						let resp = await prom;
						let result = resp.clone();
						if (result.ok) {
							let body = await result.json();
							if (body?.PremiePerMaandAanmeldingInclusiefKorting) {
								setTimeout(() => triggerNamedNotify('shoppingcart-update', {
									name: 'shoppingcart-update',
									args: {
										premie: body.PremiePerMaandAanmeldingInclusiefKorting || '',
									},
								}), 10);
							}
						}
					}
					catch(error) {
						console.warn(error);
					}
					return prom;
				} else if (url?.toLowerCase()?.endsWith('hassession')) {
					let prom = target.apply(thisArg, argumentsList);

					try {
						let resp = await prom;
						let result = resp.clone();
						if (result.ok) {
							let body = await result.json();
							if (body?.SessieVerlopen) {
								setTimeout(() => triggerNamedNotify('shoppingcart-update', {
									name: 'shoppingcart-update',
									args: {
										premie: '',
									},
								}), 10);
							}
						}
					}
					catch(error) {
						console.warn(error);
					}
					return prom;
				} else {
					return target.apply(thisArg, argumentsList);
				}
			},
		};

		window.fetch = new Proxy(fetch, fetchHandler);
	}

	if (location.pathname?.endsWith('aanmelden/bedankt') || location.pathname?.endsWith('apply/thanks')) {
		const confirmation =  store.getStoredValue('confirmation') === 'aanmelden';
		const basket = store.getStoredValue('shoppingCart');
		if (confirmation && basket?.products?.length) {
			const extended = `${Math.floor((Math.random() * 1000))}`.padStart(3, '0');
			const orderId = store.getStoredValue('aanmeldId') || `${Date.now()}${extended}`;
			putCusDimInDatalayer(18, orderId);
			addDimensionToDatalayer('order_id', orderId);
			setTimeout(async () => {
				let i = 10;
				while (typeof window?._paq?.push !== 'function' && i > 0) {
					await wait(100);
					i--;
				}
				window._paq?.push(['ecommerceOrder',
					basket.products,
					{
						orderId,
						grandTotal: basket.total,
						discount: basket.discount
					}
				]);
				store.removeStoredValue('shoppingCart');
				store.removeStoredValue('confirmation');
				store.removeStoredValue('aanmeldId');
			}, 10);
		}
	}

	processStoredPiwikdimensions();
}

export default Datalayer;